<template>
  <div ref="rootEl" class="relative" data-tour="day-time-grid" :class="readonly ? '' : 'cursor-pointer'" :style="{ height: bodyHeight + 'px' }" @click="onGridClick"
       :data-drop-key="readonly ? null : colKey" :data-drop-day="day.iso" :data-drop-teacher="col.teacher || ''" :data-min-hour="minHour">

    <!-- Hour lines -->
    <div v-for="h in allHours" :key="h"
         class="absolute left-0 right-0 border-t border-stroke-subtle dark:border-stroke-dark/60 pointer-events-none"
         :style="{ top: _hourToY(h) + 'px' }" />

    <!-- Half-hour ticks -->
    <div v-for="h in allHours.slice(0, -1)" :key="'hh' + h"
         class="absolute right-0 border-t border-stroke-subtle dark:border-stroke-dark/40 pointer-events-none"
         :style="{ top: (_hourToY(h) + hourHeight / 2) + 'px', width: '6px' }" />

    <!-- Unavailable zones (col.availability is only ever populated for a real single-teacher
         column — WeekView's merged team column passes availability: null, so this and the
         "no availability" branch below stay naturally hidden there without needing to key off
         colorByTeacher, which is about lesson-card color source, not availability shading.
         col.availability is a list of blocks — shade before the first block, any gaps
         between blocks (e.g. a lunch break), and after the last block.) -->
    <template v-if="unavailableZones">
      <div v-for="(zone, i) in unavailableZones" :key="i"
           class="absolute left-0 right-0 calendar-unavailable pointer-events-none"
           :style="{ top: zone.top + 'px', height: zone.height + 'px' }" />
    </template>
    <div v-else-if="showAvailability" class="absolute inset-0 calendar-unavailable pointer-events-none" />

    <!-- Marked time off — painted before lesson cards so any lesson still inside the
         window stacks on top and stays fully legible/clickable (see calendar-timeoff
         in tailwind.config.js for why this is a DOM-order trick, not a z-index one).
         Clickable (unless readonly) to open the remove popover — same "click the block
         to manage it" interaction Google Calendar/Outlook use for Out-of-Office blocks. -->
    <div v-for="(block, i) in timeOffLayout" :key="'to' + i"
         class="absolute left-0 right-0 calendar-timeoff"
         :class="readonly ? 'pointer-events-none' : 'cursor-pointer'"
         :style="{ top: block.top + 'px', height: block.height + 'px' }"
         @click.stop="!readonly && toggleTimeOffPopover(block, $event)">
      <span class="calendar-timeoff-label pointer-events-none">
        <span v-if="colorByTeacher" class="inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle" :style="{ background: avatarGradient(block.teacherName) }" />
        Unavailable{{ block.reason ? ' — ' + block.reason : '' }}
      </span>

      <!-- Remove popover — anchored to the tap/click point rather than a fixed corner,
           "Remove?" confirm-on-second-click (same idiom as the Signups tab's
           "Clear Slot" → "Clear?" toggle). -->
      <div v-if="openTimeOffId === block.id"
           class="absolute z-30 w-44 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-raised-dark shadow-lg p-2 pr-1.5 text-left"
           :style="{ left: popoverPos.x + 'px', top: popoverPos.y + 'px' }"
           @click.stop>
        <div class="flex items-start justify-between gap-2">
          <div class="min-w-0">
            <p class="text-xs font-medium text-fg-base dark:text-fg-base-dark">Unavailable</p>
            <p v-if="block.reason" class="text-xs text-fg-muted dark:text-fg-muted-dark mt-0.5 truncate">{{ block.reason }}</p>
          </div>
          <button class="flex-shrink-0 text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark -mt-0.5 -mr-0.5" @click="openTimeOffId = null" title="Close">
            <XMarkIcon class="h-3.5 w-3.5" />
          </button>
        </div>
        <button
          class="mt-2 w-full text-xs px-2 py-1 rounded transition-colors"
          :class="confirmDeleteId === block.id
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50'"
          :disabled="deletingId === block.id"
          @click="confirmOrDeleteTimeOff(block)"
        >{{ deletingId === block.id ? 'Removing…' : confirmDeleteId === block.id ? 'Remove?' : 'Remove' }}</button>
      </div>
    </div>

    <!-- Lesson cards (side-by-side when overlapping). Ghosts are dashed and faded —
         clearly not a lesson yet: pending invites (blue, utils/invite_holds.py — the
         family has a registration link) and lesson requests (amber, useRequestGhosts.js
         — the teacher responds from the signup card they open). -->
    <div
      v-for="{ lesson, lane, numCols, ghost } in lessonLayout" :key="lesson.id"
      :data-tour="ghost ? null : 'lesson-slot'"
      class="absolute rounded overflow-hidden"
      :class="!ghost ? ['shadow-sm', canDrag(lesson) && 'select-none [-webkit-touch-callout:none]', lessonDrag?.lesson.id === lesson.id && 'opacity-40'] : lesson.request
        ? 'border border-dashed border-amber-500 dark:border-amber-400 bg-amber-50/70 dark:bg-amber-950/40 cursor-pointer'
        : 'border border-dashed border-blue-400 dark:border-blue-500 bg-blue-50/60 dark:bg-blue-950/40 cursor-pointer'"
      :style="{
        top: _timeToY(lesson.lesson_time) + 'px',
        height: Math.max(_durationToH(lesson.duration_minutes || 30), 18) + 'px',
        left: `calc(${(lane / numCols) * 100}% + 2px)`,
        width: `calc(${(1 / numCols) * 100}% - 4px)`,
        ...(ghost ? {} : cardStyle(colorByTeacher ? lesson.teacher_name : col.teacher))
      }"
      @click.stop="ghost && toggleGhostPopover(lesson, $event)"
      @pointerdown="!ghost && canDrag(lesson) && startLessonPress(lesson, $event)"
    >
      <div v-if="ghost" class="px-1.5 py-0.5 text-[11px] leading-tight opacity-80"
           :class="lesson.request ? 'text-amber-900 dark:text-amber-200' : 'text-blue-800 dark:text-blue-200'">
        <!-- Name · instrument, then age — the age wraps to its own line when the column is too narrow. -->
        <div class="flex flex-wrap gap-x-1.5">
          <p class="truncate min-w-0 max-w-full">
            <span class="font-medium">{{ lesson.student_name || lesson.contact_name || (lesson.request ? 'New student' : 'Invited student') }}</span>
            <template v-if="lesson.request && lesson.instrument"> · {{ lesson.instrument }}</template>
          </p>
          <p v-if="lesson.request && lesson.age" class="whitespace-nowrap">Age {{ lesson.age }}</p>
        </div>
        <p class="truncate italic">{{ lesson.request ? REQUEST_LABELS[lesson.kind] : 'Awaiting registration' }}</p>
      </div>
      <LessonCard v-else :lesson="lesson" @lessons-removed="emit('lessons-removed', $event)" />
      <!-- Bottom-edge grip: drag to change the length (useLessonDrag.js). -->
      <div v-if="!ghost && canResize(lesson)"
           class="absolute left-0 right-0 bottom-0 h-2 cursor-ns-resize touch-none"
           @pointerdown="startLessonResize(lesson, $event, { key: colKey, day: day.iso, teacher: col.teacher || null, time: lesson.lesson_time }, maxDuration(lesson))" />
    </div>

    <!-- Drag preview (useLessonDrag.js) — drawn by whichever column the pointer is over. -->
    <div v-if="dropPreview"
         class="absolute left-0.5 right-0.5 z-20 rounded border-2 border-dashed border-blue-500 bg-blue-100/60 dark:bg-blue-900/40 pointer-events-none px-1.5 py-0.5 text-[11px] font-medium text-blue-900 dark:text-blue-100"
         :style="{ top: _timeToY(dropPreview.time) + 'px', height: Math.max(_durationToH(dropPreview.duration || dropPreview.lesson.duration_minutes || 30), 18) + 'px' }">
      {{ dropPreview.duration ? `${dropPreview.duration} min` : fmtTime(dropPreview.time) }}
    </div>

    <!-- Drop confirm — nothing moves until a button is clicked. -->
    <div v-if="myDropPrompt"
         class="absolute z-30 w-56 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-raised-dark shadow-lg p-2.5 text-left"
         :style="{ left: '2px', top: Math.max(0, Math.min(_timeToY(myDropPrompt.time) + _durationToH(myDropPrompt.duration || myDropPrompt.lesson.duration_minutes || 30) + 4, bodyHeight - DROP_POPOVER_H)) + 'px' }"
         @click.stop>
      <p v-if="myDropPrompt.duration" class="text-xs font-medium text-fg-base dark:text-fg-base-dark truncate">
        Change {{ myDropPrompt.lesson.student_name || 'lesson' }} to {{ myDropPrompt.duration }} min?
      </p>
      <p v-else class="text-xs font-medium text-fg-base dark:text-fg-base-dark truncate">
        Move {{ myDropPrompt.lesson.student_name || 'lesson' }} to {{ _shortDate(myDropPrompt.day) }} · {{ fmtTime(myDropPrompt.time) }}?
      </p>
      <p v-if="resizePrice" class="text-[11px] text-fg-secondary dark:text-fg-secondary-dark mt-0.5">{{ resizePrice }}</p>
      <p class="text-[11px] text-fg-muted dark:text-fg-muted-dark mt-0.5">The family gets one email about your changes after 15 minutes.</p>
      <p v-if="dropError" class="text-[11px] text-red-600 dark:text-red-400 mt-1">{{ dropError }}</p>
      <div class="mt-2 flex flex-col gap-1.5">
        <button class="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 transition-colors"
                :disabled="dropBusy" @click="doDrop(false)">{{ dropBusy === 'one' ? 'Saving…' : `${dropVerb} this lesson` }}</button>
        <button v-if="myDropPrompt.lesson.lesson_series_id"
                class="text-xs px-2 py-1 rounded bg-surface-raised dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark disabled:opacity-60 transition-colors"
                :disabled="dropBusy" @click="doDrop(true)">{{ dropBusy === 'all' ? 'Saving…' : `${dropVerb} this and all future lessons` }}</button>
        <button class="text-xs px-2 py-1 rounded text-fg-secondary dark:text-fg-secondary-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors"
                :disabled="dropBusy" @click="cancelDrop">Cancel</button>
      </div>
    </div>

    <!-- Ghost popover — grid-level (the cards clip their overflow), same idiom as time off. -->
    <div v-if="openGhost"
         class="absolute z-30 w-56 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-raised-dark shadow-lg p-2.5 text-left"
         :style="{ left: popoverPos.x + 'px', top: popoverPos.y + 'px' }"
         @click.stop>
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="text-xs font-medium text-fg-base dark:text-fg-base-dark">Awaiting registration</p>
          <p class="text-xs text-fg-secondary dark:text-fg-secondary-dark mt-0.5 truncate">
            {{ [openGhost.student_name, openGhost.instrument].filter(Boolean).join(' · ') || 'Invited student' }}
          </p>
          <p v-if="openGhost.contact_name && openGhost.contact_name !== openGhost.student_name" class="text-xs text-fg-muted dark:text-fg-muted-dark truncate">{{ openGhost.contact_name }}</p>
        </div>
        <button class="flex-shrink-0 text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark -mt-0.5 -mr-0.5" @click="openGhost = null" title="Close">
          <XMarkIcon class="h-3.5 w-3.5" />
        </button>
      </div>
      <p class="text-[11px] text-fg-muted dark:text-fg-muted-dark mt-1.5">
        Registration link sent. This time is held until {{ fmtEastern(openGhost.expires_at) }}, then it opens back up.
      </p>
      <div v-if="canManageInvites" class="mt-2 flex gap-1.5">
        <button class="flex-1 text-xs px-2 py-1 rounded bg-surface-raised dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors"
                :disabled="ghostBusy" @click="ghostAction('resend')">{{ ghostBusy === 'resend' ? 'Sending…' : 'Resend link' }}</button>
        <button class="flex-1 text-xs px-2 py-1 rounded transition-colors"
                :class="confirmGhostCancel ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50'"
                :disabled="ghostBusy" @click="ghostAction('cancel')">{{ ghostBusy === 'cancel' ? 'Cancelling…' : confirmGhostCancel ? 'Cancel invite?' : 'Cancel invite' }}</button>
      </div>
    </div>

    <!-- Now indicator -->
    <div v-if="showNowLine" class="absolute left-0 right-0 h-0.5 bg-red-500 pointer-events-none z-20" :style="{ top: nowY + 'px' }" />

    <!-- Empty-space tap/click chooser — anchored to the tap point, same idiom as the
         remove-unavailable popover above. Replaces the old "click always creates a
         lesson" behavior so a stray tap can't misfire; the chosen action gets the
         nearest-15-min time the user actually tapped. -->
    <div v-if="gridChoice"
         data-tour="calendar-grid-choice"
         class="absolute z-30 w-40 rounded-lg border border-stroke dark:border-stroke-dark bg-surface dark:bg-surface-raised-dark shadow-lg py-1 text-left"
         :style="{ left: gridChoice.x + 'px', top: gridChoice.y + 'px' }"
         @click.stop>
      <div class="flex items-center justify-between gap-2 pl-3 pr-1.5 pt-0.5 pb-1.5 border-b border-stroke-subtle dark:border-stroke-dark">
        <p class="text-[11px] font-medium whitespace-nowrap text-fg-faint dark:text-fg-faint-dark">{{ gridChoiceLabel }}</p>
        <button data-tour="calendar-grid-choice-close-btn" class="flex-shrink-0 text-fg-faint hover:text-fg-secondary dark:hover:text-fg-secondary-dark" @click="gridChoice = null" title="Close">
          <XMarkIcon class="h-3.5 w-3.5" />
        </button>
      </div>
      <button class="w-full text-left text-xs px-3 py-1.5 text-fg-base dark:text-fg-base-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors"
              @click="chooseGridAction('create-lesson')">New Lesson</button>
      <button class="w-full text-left text-xs px-3 py-1.5 text-fg-base dark:text-fg-base-dark hover:bg-surface-hover dark:hover:bg-surface-hover-dark transition-colors"
              @click="chooseGridAction('mark-time-off')">Mark Unavailable</button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { hourHeight, hourToY, timeToY, durationToH, parseLessonTime, yToTime, fmtTime, lessonLockTier, notifyLessonsChanged } from '@/composables/useCalendarData.js'
import { lessonDrag, dropPrompt, startLessonPress, startLessonResize, confirmDrop, DURATIONS } from '@/composables/useLessonDrag.js'
import { useDemoMode } from '@/composables/useDemoMode'
import { avatarGradient } from '@/composables/useAvatarGradient.js'
import { useNow } from '@/composables/useNow.js'
import { useApi } from '@/composables/useApi.js'
import { calendarColors } from '@/theme/calendarColors.js'
import LessonCard from './LessonCard.vue'

const props = defineProps({
  day:           { type: Object,  required: true },
  col:           { type: Object,  required: true },  // { teacher, availability }
  lessons:       { type: Array,   default: () => [] },
  minHour:       { type: Number,  required: true },
  maxHour:       { type: Number,  required: true },
  isDark:        { type: Boolean, default: false },
  colorByTeacher: { type: Boolean, default: false },  // color/label lesson cards by their own teacher rather than col.teacher (multi-teacher columns, or a locked single-teacher column)
  showAvailability: { type: Boolean, default: true },  // shade unavailable hours for a real single-teacher column; WeekView's merged team column has no single availability to show
  readonly:      { type: Boolean, default: false },  // suppress click-to-create (read-only embed)
  timeOffBlocks: { type: Array, default: () => [] },  // this day's marked-time-off windows: [{ start: 'HH:MM', end: 'HH:MM', teacherName, reason }]
  ghosts:        { type: Array, default: () => [] },  // this column's pending invites (/…-ghosts endpoints)
  canManageInvites: { type: Boolean, default: false },  // admin: resend / cancel from the ghost popover
})

const emit = defineEmits(['create-lesson', 'mark-time-off', 'lessons-removed', 'time-off-deleted', 'ghosts-changed', 'open-request'])
const { fetchApi } = useApi()

// Empty background clicks open a small "New Lesson" / "Mark Unavailable" chooser,
// anchored to the tap point, instead of creating a lesson outright — clicks on a
// lesson card (or anything else absorbing the event) never bubble here as
// e.target === e.currentTarget. Also dismisses the remove-unavailable popover, so a
// stray background click after opening one doesn't leave it stuck open.
function onGridClick(e) {
  openGhost.value = null
  openTimeOffId.value = null
  confirmDeleteId.value = null
  gridChoice.value = null
  if (props.readonly) return
  if (e.target !== e.currentTarget) return
  const rect = e.currentTarget.getBoundingClientRect()
  gridChoice.value = {
    time: yToTime(e.offsetY, props.minHour),
    x: Math.max(0, Math.min(e.clientX - rect.left, rect.width - GRID_CHOICE_W)),
    y: Math.max(0, Math.min(e.clientY - rect.top, rect.height - GRID_CHOICE_H)),
  }
}

// "8/18 - 2:15pm" — date before time, matching Google/Outlook's own event-popover
// convention (they lead with the date, e.g. "Tuesday, August 18 · 2:15pm").
const _shortDate = iso => {
  const [, m, d] = iso.split('-').map(Number)
  return `${m}/${d}`
}
const gridChoiceLabel = computed(() =>
  gridChoice.value ? `${_shortDate(props.day.iso)} - ${fmtTime(gridChoice.value.time)}` : ''
)

function chooseGridAction(kind) {
  emit(kind, { date: props.day.iso, teacher: props.col.teacher, time: gridChoice.value.time })
  gridChoice.value = null
}

// ── Empty-space tap/click chooser ───────────────────────────────────────────────
const gridChoice = ref(null)
const GRID_CHOICE_W = 160  // w-40
const GRID_CHOICE_H = 90   // approx rendered height, for bottom-edge clamping

// Closes both popovers on any click outside this grid's own DOM — covers clicks on
// another day's/teacher's column (a separate DayTimeGrid instance), the calendar
// toolbar, or anywhere else on the page. Clicks inside this instance are already
// handled locally (onGridClick / toggleTimeOffPopover / the popovers' own buttons),
// so this only ever fires for genuinely outside clicks — a bubble-phase listener
// never even sees a click a local handler already stopped with @click.stop.
const rootEl = ref(null)
function onDocumentClick(e) {
  if (rootEl.value && !rootEl.value.contains(e.target)) {
    if (myDropPrompt.value && !dropBusy.value) dropPrompt.value = null
    openGhost.value = null
    openTimeOffId.value = null
    confirmDeleteId.value = null
    gridChoice.value = null
  }
}
onMounted(() => document.addEventListener('click', onDocumentClick))
onUnmounted(() => document.removeEventListener('click', onDocumentClick))

// ── Drag a lesson to a new time (useLessonDrag.js) ─────────────────────────────
// Open lessons from today on; paid/locked ones can't be edited, and past ones are history.
const todayIso = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' })
const canDrag = lesson => !props.readonly && lessonLockTier(lesson) === 'open' && lesson.lesson_date >= todayIso
const colKey = computed(() => `${props.day.iso}|${props.col.teacher || ''}`)
const dropPreview = computed(() => {
  const t = lessonDrag.value?.target
  return t && t.key === colKey.value ? { ...t, lesson: lessonDrag.value.lesson } : null
})
const myDropPrompt = computed(() => dropPrompt.value?.key === colKey.value ? dropPrompt.value : null)
const DROP_POPOVER_H = 150
const dropBusy = ref(null)
const dropError = ref(null)
watch(dropPrompt, () => { dropError.value = null })

const cancelDrop = () => { dropPrompt.value = null }
const dropVerb = computed(() => myDropPrompt.value?.duration ? 'Change' : 'Move')

// Resize: a membership is billed flat, so its lessons' length is changed on the membership.
const canResize = lesson => canDrag(lesson) && lesson.billing_route !== 'membership'
// Minutes until this teacher's next lesson that day (the grip can't stretch into it).
function maxDuration(lesson) {
  const start = parseLessonTime(lesson.lesson_time)
  const startMin = start.h * 60 + start.m
  const next = props.lessons
    .filter(l => l.id !== lesson.id && l.teacher_name === lesson.teacher_name)
    .map(l => { const t = parseLessonTime(l.lesson_time); return t ? t.h * 60 + t.m : null })
    .filter(m => m != null && m > startMin)
  return Math.max(Math.min(DURATIONS.at(-1), ...next.map(m => m - startMin)), DURATIONS[0])
}

// The re-price /lesson-edits applies (same $/hour). Admins only — teachers never see prices.
const effectivePermissions = useDemoMode().effectivePermissionsFor(() =>
  JSON.parse(localStorage.getItem('user') || 'null')?.permissions)
const isAdmin = computed(() => effectivePermissions.value.includes('admin'))
const resizePrice = computed(() => {
  const p = myDropPrompt.value
  const old = p?.lesson.price_cents
  if (!p?.duration || !isAdmin.value || old == null || p.lesson.billing_route === 'comp') return null
  const now = Math.round(old / (p.lesson.duration_minutes || 30) * p.duration)
  return `$${(now / 100).toFixed(2)} per lesson (was $${(old / 100).toFixed(2)})`
})

async function doDrop(allFuture) {
  dropBusy.value = allFuture ? 'all' : 'one'
  dropError.value = null
  try {
    await confirmDrop(allFuture)
    dropPrompt.value = null
    notifyLessonsChanged()
  } catch (e) {
    dropError.value = e.message || 'Could not move the lesson.'
  } finally {
    dropBusy.value = null
  }
}

// ── Remove-unavailable-block popover ────────────────────────────────────────────
const openTimeOffId = ref(null)
const confirmDeleteId = ref(null)
const deletingId = ref(null)
const popoverPos = ref({ x: 0, y: 0 })
const POPOVER_W = 176  // w-44
const POPOVER_H = 76   // approx rendered height, for bottom-edge clamping

function toggleTimeOffPopover(block, event) {
  gridChoice.value = null
  confirmDeleteId.value = null
  if (openTimeOffId.value === block.id) {
    openTimeOffId.value = null
    return
  }
  openTimeOffId.value = block.id
  // Anchor to the tap/click point, relative to the block (event.currentTarget is the
  // block div itself since this handler is bound there), clamped so the popover never
  // renders past the column's edges.
  const rect = event.currentTarget.getBoundingClientRect()
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  popoverPos.value = {
    x: Math.max(0, Math.min(x, rect.width - POPOVER_W)),
    y: Math.max(0, Math.min(y, rect.height - POPOVER_H)),
  }
}

// ── Ghost (pending invite) popover ──────────────────────────────────────────────
const openGhost = ref(null)
const confirmGhostCancel = ref(false)
const ghostBusy = ref(null)
const GHOST_POPOVER_W = 224  // w-56
const GHOST_POPOVER_H = 130

const REQUEST_LABELS = { requested: 'Request · respond', family_suggested: 'Family suggested · respond', you_offered: 'You offered · waiting' }

function toggleGhostPopover(ghost, event) {
  if (ghost.request) { emit('open-request', ghost.signup); return }
  gridChoice.value = null
  openTimeOffId.value = null
  confirmGhostCancel.value = false
  if (openGhost.value?.id === ghost.id) { openGhost.value = null; return }
  openGhost.value = ghost
  const rect = rootEl.value.getBoundingClientRect()
  popoverPos.value = {
    x: Math.max(0, Math.min(event.clientX - rect.left, rect.width - GHOST_POPOVER_W)),
    y: Math.max(0, Math.min(event.clientY - rect.top, rect.height - GHOST_POPOVER_H)),
  }
}

const fmtEastern = iso => new Date(iso).toLocaleString('en-US', {
  timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})

async function ghostAction(kind) {
  if (ghostBusy.value) return
  if (kind === 'cancel' && !confirmGhostCancel.value) { confirmGhostCancel.value = true; return }
  const id = openGhost.value.series_id
  ghostBusy.value = kind
  try {
    if (kind === 'resend') await fetchApi(`/api/lesson-series/${id}/resend-invite`, { method: 'POST' })
    else await fetchApi(`/api/lesson-series/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'cancelled', invite_status: 'cancelled' }) })
    openGhost.value = null
    emit('ghosts-changed')
  } catch {
    // leave the popover open so the user can retry
  } finally {
    ghostBusy.value = null
    confirmGhostCancel.value = false
  }
}

async function confirmOrDeleteTimeOff(block) {
  if (deletingId.value) return
  if (confirmDeleteId.value !== block.id) {
    confirmDeleteId.value = block.id
    return
  }
  deletingId.value = block.id
  try {
    await fetchApi(`/api/teacher-cancellations/${block.id}`, { method: 'DELETE' })
    emit('time-off-deleted', block.id)
    openTimeOffId.value = null
  } catch {
    // leave the popover open with "Remove?" so the user can retry
  } finally {
    deletingId.value = null
    confirmDeleteId.value = null
  }
}

const bodyHeight = computed(() => (props.maxHour - props.minHour) * hourHeight.value)
const allHours   = computed(() => Array.from({ length: props.maxHour - props.minHour + 1 }, (_, i) => props.minHour + i))

const _hourToY    = h    => hourToY(h, props.minHour)
const _timeToY    = t    => timeToY(t, props.minHour)
const _durationToH = mins => durationToH(mins)

const unavailableZones = computed(() => {
  const blocks = props.col.availability
  if (!blocks || !blocks.length) return null
  const sorted = [...blocks].sort((a, b) => a.start.localeCompare(b.start))
  const zones = []
  const firstY = _timeToY(sorted[0].start)
  if (firstY > 0) zones.push({ top: 0, height: firstY })
  for (let i = 0; i < sorted.length - 1; i++) {
    const gapTop = _timeToY(sorted[i].end)
    const gapBottom = _timeToY(sorted[i + 1].start)
    if (gapBottom > gapTop) zones.push({ top: gapTop, height: gapBottom - gapTop })
  }
  const lastY = _timeToY(sorted[sorted.length - 1].end)
  if (lastY < bodyHeight.value) zones.push({ top: lastY, height: bodyHeight.value - lastY })
  return zones
})

// Clip each time-off window to this day's visible [minHour, maxHour] range and
// convert to pixel top/height, same convention as unavailableZones above.
const timeOffLayout = computed(() => {
  return props.timeOffBlocks.map(block => {
    const top = Math.max(_timeToY(block.start), 0)
    const bottom = Math.min(_timeToY(block.end), bodyHeight.value)
    return {
      id: block.id,
      top,
      height: Math.max(bottom - top, 0),
      teacherName: block.teacherName,
      reason: block.reason,
    }
  }).filter(b => b.height > 0)
})

const { nowHourFraction } = useNow()
const showNowLine = computed(() =>
  props.day.isToday && nowHourFraction.value >= props.minHour && nowHourFraction.value <= props.maxHour
)
const nowY = computed(() => _hourToY(nowHourFraction.value))

// Assign each lesson a lane so overlapping lessons render side by side.
// Greedy sweep: place each lesson in the first lane whose last lesson ended.
const lessonLayout = computed(() => {
  const items = [
    ...props.lessons.map(lesson => ({ lesson, ghost: false })),
    ...props.ghosts.map(lesson => ({ lesson, ghost: true })),
  ].map(({ lesson, ghost }) => {
    const p = parseLessonTime(lesson.lesson_time)
    const startMin = p ? p.h * 60 + p.m : 0
    const endMin   = startMin + (lesson.duration_minutes || 30)
    return { lesson, ghost, startMin, endMin, lane: 0 }
  }).sort((a, b) => a.startMin - b.startMin)

  const laneEnds = []
  for (const item of items) {
    let lane = laneEnds.findIndex(end => item.startMin >= end)
    if (lane === -1) lane = laneEnds.length
    item.lane = lane
    laneEnds[lane] = item.endMin
  }

  return items.map(item => {
    const concurrent = items.filter(
      o => o.startMin < item.endMin && o.endMin > item.startMin
    )
    const numCols = Math.max(...concurrent.map(o => o.lane)) + 1
    return { lesson: item.lesson, ghost: item.ghost, lane: item.lane, numCols }
  })
})

const cardStyle = name => {
  const neutral = props.isDark ? calendarColors.cardNeutralDark : calendarColors.cardNeutral
  return { background: `${avatarGradient(name, 'to bottom')} left/3px 100% no-repeat, ${neutral}` }
}
</script>
